import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import { supabase } from './lib/supabase';
import ErrorBoundary from './components/ErrorBoundary';

// ⬇️ Pages
import { Landing } from './pages/Landing';
import { Confirmation } from './pages/Confirmation';
import { ImpactIntentions } from './pages/ImpactIntentions';
import { CoreWord } from './pages/CoreWord';
import { CoreValues } from './pages/CoreValues';
import { CoreValueConfirmation } from './pages/CoreValueConfirmation';
import { Welcome } from './pages/Welcome';
import { Dashboard } from './pages/Dashboard';
import { Profile } from './pages/Profile';
import { UpdateCoreValues } from './pages/UpdateCoreValues';
import { TaskList } from './pages/TaskList';
import { TaskOverview } from './pages/TaskOverview';
import { SyncMonitor } from './pages/SyncMonitor';
import { Journal } from './pages/Journal';
import { JournalComplete } from './pages/JournalComplete';
import { JournalHistory } from './pages/JournalHistory';
import { ScoringExplanation } from './pages/ScoringExplanation';
import { JournalingOverview } from './pages/JournalingOverview';
import { RepetitionPower } from './pages/RepetitionPower';
import { WeeklyInsights } from './pages/WeeklyInsights';
import { WeeklySummaries } from './pages/WeeklySummaries';
import { LessonsHistory } from './pages/LessonsHistory';
import { Content } from './pages/Content';
import { Pricing } from './pages/Pricing';
import { ResetPassword } from './pages/ResetPassword';
import { PaymentConfirmation } from './pages/PaymentConfirmation';
import { FeatureAccessProvider } from './context/FeatureAccessContext';
import { Payment } from './pages/Payment';
import DashboardRoutes from './routes/DashboardRoutes';
import { DashGrowthLoop } from './pages/DashGrowthLoop';
import { DashTools } from './pages/DashTools';
import { CouponConfirmation } from './pages/CouponConfirmation';
import { WeeklyReview } from './pages/WeeklyReview';
import { Introduction } from './pages/Introduction';
import { Registration } from './pages/Registration';
import { LoadingSpinner } from './components/LoadingSpinner';
import { CreatePlan } from './pages/CreatePlan';
import { CreateLadder } from './pages/CreateLadder';
import { CreateNestedLadder } from './pages/CreateNestedLadder';
import ImportNotionData from './pages/ImportNotionData';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          throw new Error('Session error: ' + sessionError.message);
        }
        
        if (!data.session) {
          throw new Error('No active session');
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setError(error instanceof Error ? error.message : 'Authentication failed');
        
        // Clean up and redirect
        await supabase.auth.signOut();
        localStorage.clear();
        window.location.href = '/';
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <div className="text-[#236caf] text-lg font-medium mt-4">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full text-center">
          <h2 className="text-xl font-bold text-red-600 mb-4">Authentication Error</h2>
          <p className="text-gray-700 mb-6">{error}</p>
          <button 
            onClick={() => window.location.href = '/'}
            className="px-4 py-2 bg-[#236caf] text-white rounded-lg hover:bg-[#004060] transition-colors"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return <ErrorBoundary>{children}</ErrorBoundary>;
}

function App() {
  return (
    <ErrorBoundary>
      <FeatureAccessProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/confirmation" element={<Confirmation />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/introduction" element={<Introduction />} />
            <Route path="/registration" element={<Registration />} />

            {/* Protected routes */}
            <Route path="/pricing" element={<ProtectedRoute><Pricing /></ProtectedRoute>} />
            <Route path="/payment-confirmation" element={<ProtectedRoute><PaymentConfirmation /></ProtectedRoute>} />
            <Route path="/welcome" element={<ProtectedRoute><Welcome /></ProtectedRoute>} />
            <Route path="/impactintentions" element={<ProtectedRoute><ImpactIntentions /></ProtectedRoute>} />
            <Route path="/core-word" element={<ProtectedRoute><CoreWord /></ProtectedRoute>} />
            <Route path="/core-values" element={<ProtectedRoute><CoreValues /></ProtectedRoute>} />
            <Route path="/core-value-confirmation" element={<ProtectedRoute><CoreValueConfirmation /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/update-core-values" element={<ProtectedRoute><UpdateCoreValues /></ProtectedRoute>} />
            <Route path="/tasks" element={<ProtectedRoute><TaskList /></ProtectedRoute>} />
            <Route path="/task-overview" element={<ProtectedRoute><TaskOverview /></ProtectedRoute>} />
            <Route path="/sync-monitor" element={<ProtectedRoute><SyncMonitor /></ProtectedRoute>} />
            <Route path="/journal/new" element={<ProtectedRoute><Journal /></ProtectedRoute>} />
            <Route path="/journal/complete" element={<ProtectedRoute><JournalComplete /></ProtectedRoute>} />
            <Route path="/journal/history" element={<ProtectedRoute><JournalHistory /></ProtectedRoute>} />
            <Route path="/scoring" element={<ProtectedRoute><ScoringExplanation /></ProtectedRoute>} />
            <Route path="/journaling-overview" element={<ProtectedRoute><JournalingOverview /></ProtectedRoute>} />
            <Route path="/repetition-power" element={<ProtectedRoute><RepetitionPower /></ProtectedRoute>} />
            <Route path="/weekly-insights" element={<ProtectedRoute><WeeklyInsights /></ProtectedRoute>} />
            <Route path="/weekly-summaries" element={<ProtectedRoute><WeeklySummaries /></ProtectedRoute>} />
            <Route path="/lessons-history" element={<ProtectedRoute><LessonsHistory /></ProtectedRoute>} />
            <Route path="/content" element={<ProtectedRoute><Content /></ProtectedRoute>} />
            <Route path="/dashboard/*" element={<ProtectedRoute><DashboardRoutes /></ProtectedRoute>} />
            <Route path="/payment" element={<ProtectedRoute><Payment /></ProtectedRoute>} />
            <Route path="/dashgrowthloop" element={<ProtectedRoute><DashGrowthLoop /></ProtectedRoute>} />
            <Route path="/dash-growth-loop" element={<ProtectedRoute><DashGrowthLoop /></ProtectedRoute>} />
            <Route path="/dashtools" element={<ProtectedRoute><DashTools /></ProtectedRoute>} />
            <Route path="/create-plan" element={<ProtectedRoute><CreatePlan /></ProtectedRoute>} />
            <Route path="/create-ladder/:goalId" element={<ProtectedRoute><CreateLadder /></ProtectedRoute>} />
            <Route path="/create-nested-ladder/:parentStepId" element={<ProtectedRoute><CreateNestedLadder /></ProtectedRoute>} />
            <Route path="/import-notion" element={<ProtectedRoute><ImportNotionData /></ProtectedRoute>} />
            <Route path="/weeklyreview" element={<ProtectedRoute><WeeklyReview /></ProtectedRoute>} />
            <Route path="/couponconfirmation" element={<CouponConfirmation />} />
          </Routes>
        </BrowserRouter>
      </FeatureAccessProvider>
    </ErrorBoundary>
  );
}

export default App;